import type { common, resources } from '../gads'
import type { GadsFieldsRequired } from '../utils/global'

import type { Section } from '.'

export const ProductStatus = {
    zeroImpressions: 'zeroImpressions',
    learning: 'learning',
    topConverters: 'topConverters',
    converters: 'converters',
    nonConverters: 'nonConverters',
    poorPerformers: 'poorPerformers',
} as const
export type ProductStatus = (typeof ProductStatus)[keyof typeof ProductStatus]

export type Product<T extends { hasStatus: boolean } = { hasStatus: false }> = {
    campaign: GadsFieldsRequired<
        resources.Campaign,
        | 'resource_name'
        | 'name'
        | 'advertising_channel_type'
        | 'status'
        | 'experiment_type'
        | 'serving_status'
    >
    metrics: GadsFieldsRequired<
        common.Metrics,
        | 'cost_micros'
        | 'impressions'
        | 'all_conversions'
        | 'all_conversions_value'
        | 'impressions'
        | 'clicks'
    >
    segments: GadsFieldsRequired<common.Segments, 'product_title' | 'product_item_id'>
} & (T extends { hasStatus: true } ? { status: ProductStatus } : { status?: undefined })

export interface ProductMetricsByStatus {
    count: number
    cost: number
    conversions: number
    conversionsValue: number
}

export interface ProductMetricsTotals {
    totalProductsWithImpressions: number
    totalCost: number
    totalConversions: number
    totalConversionsValue: number
    avgCpa: number
    avgRoas: number
}

export interface ProductPerformanceScoreDetails {
    zeroImpressions: ProductMetricsByStatus
    learning: ProductMetricsByStatus
    cpaModeData: {
        topConverters: ProductMetricsByStatus
        converters: ProductMetricsByStatus
        nonConverters: ProductMetricsByStatus
        poorPerformers: ProductMetricsByStatus
    }
    roasModeData: {
        topConverters: ProductMetricsByStatus
        converters: ProductMetricsByStatus
        nonConverters: ProductMetricsByStatus
        poorPerformers: ProductMetricsByStatus
    }
    totals: ProductMetricsTotals
    previous30DaysTotals: ProductMetricsTotals
}

export type BreakdownProduct = {
    campaignResourceName: string
    campaignName: string
    advertisingChannelType: Product['campaign']['advertising_channel_type']
    status: Product<{ hasStatus: true }>['status']
    productName: string
    productId: Product['segments']['product_item_id']
    costMicros: number
    conversions: number
    conversionsValue: number
    impressions: number
}

export interface ProductPerformanceScoreBreakdown {
    totalCostPerCampaign: Record<string, number>
    zeroImpressions: BreakdownProduct[]
    learning: BreakdownProduct[]
    cpaModeData: {
        topConverters: BreakdownProduct[]
        converters: BreakdownProduct[]
        nonConverters: BreakdownProduct[]
        poorPerformers: BreakdownProduct[]
    }
    roasModeData: {
        topConverters: BreakdownProduct[]
        converters: BreakdownProduct[]
        nonConverters: BreakdownProduct[]
        poorPerformers: BreakdownProduct[]
    }
}

export interface ProductPerformanceScoreSection extends Section {
    type: 'productPerformance'
    details: ProductPerformanceScoreDetails
    breakdown: ProductPerformanceScoreBreakdown
}
